import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";
import { FaSearch, FaPrint, FaFilter, FaEye, FaCheck } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/stnOutward";

/* ---------------- helpers ---------------- */
const toStr = (v, d = "") => (v === null || v === undefined ? d : String(v));

// "2026-09-19T00:00:00.000Z" -> "19/09/2026"
// Uses the date part of the string as-is, so the timezone can't shift the day.
// A value that is already dd/MM/yyyy has no "-" and is returned unchanged.
const formatDate = (value) => {
  if (!value) return "";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : String(value);
};
const STNOutwardApprove = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  /* ---------------- session-like values from localStorage ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const SESSION = useMemo(
    () => ({
      branchid: parseInt(branchId) || 0,
      loginuserid: parseInt(userId) || 0,
    }),
    [branchId, userId]
  );

  /* ---------------- UI state ---------------- */
  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [inwardStore, setInwardStore] = useState("Select");
  const [status, setStatus] = useState("Select");
  const [type, setType] = useState("Select");
  const [stnOutwardNo, setStnOutwardNo] = useState("");

  /* ---------------- data state ---------------- */
  const [storeList, setStoreList] = useState([]);
  const [records, setRecords] = useState([]);
  const [grandTotal, setGrandTotal] = useState("0.00");
  const [currency, setCurrency] = useState({ code: "en-IN", symbol: "₹" });
  const [autoSuggestions, setAutoSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  /* ---------------- loading ---------------- */
  const [loading, setLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  /* ---------------- init ---------------- */
  const loadInit = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: SESSION.branchid,
          userID: SESSION.loginuserid,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const stores = res.data.stores || res.data.data?.stores || [];
        const filters = res.data.filters || res.data.data?.filters || {};

        setStoreList(stores);
        setFromDate(filters.fromDate || "");
        setToDate(filters.toDate || "");
        setInwardStore(filters.requestedStore || "Select");
        setStatus(filters.status || "Select");
        setType(filters.type || "Select");
        setStnOutwardNo(filters.stnReqNo || "");
      }
    } catch (err) {
      toast.error("Failed to load initial page data");
    } finally {
      setPageLoaded(true);
    }
  }, [SESSION.branchid, SESSION.loginuserid, commonHeaders]);

  /* ---------------- list / FillGrid ---------------- */
  const fillGrid = useCallback(async () => {
    if (!SESSION.branchid || !SESSION.loginuserid) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchID: SESSION.branchid,
          stnReqNo: stnOutwardNo,
          fromDate,
          toDate,
          requestedStore: inwardStore,
          status,
          type,
          userID: SESSION.loginuserid,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const rows = res.data.rows || [];
        const total = res.data.grandTotal || "0.00";
        const cur = res.data.currency || { code: "en-IN", symbol: "₹" };

        setRecords(rows);
        setGrandTotal(total);
        setCurrency(cur);

        if (rows.length === 0) {
          toast.info("No records found");
        }
      } else {
        setRecords([]);
        setGrandTotal("0.00");
      }
    } catch (err) {
      setRecords([]);
      setGrandTotal("0.00");
      toast.error("Failed to fetch approval records");
    } finally {
      setLoading(false);
    }
  }, [
    SESSION.branchid,
    SESSION.loginuserid,
    stnOutwardNo,
    fromDate,
    toDate,
    inwardStore,
    status,
    type,
    commonHeaders,
  ]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (pageLoaded) {
      fillGrid();
    }
  }, [pageLoaded, fillGrid]);

  /* ---------------- search ---------------- */
  const handleSearch = () => {
    fillGrid();
  };

  /* ---------------- autocomplete ---------------- */
  const handleOutwardNoChange = async (val) => {
    setStnOutwardNo(val);

    if (!val || val.trim().length < 2) {
      setAutoSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/autocomplete`, {
        params: {
          branchid: SESSION.branchid,
          prefix: val,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const suggestions = res.data.suggestions || res.data.data || [];
        setAutoSuggestions(suggestions);
        setShowSuggestions(true);
      } else {
        setAutoSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      setAutoSuggestions([]);
      setShowSuggestions(false);
    }
  };

  /* ---------------- view ---------------- */
  const handleView = async (row) => {
    const outwardId = row._id || row.fld_STN_OutwardID || row.STNOutwardID;
    const rowBranchId = row._branchId || row.fld_BranchID || row.branchId || "";

    if (!outwardId) {
      toast.error("Outward ID missing");
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/view/${outwardId}`, {
        params: { branchId: rowBranchId || SESSION.branchid },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const params = new URLSearchParams({
          outwardId: String(outwardId),
          branchId: String(rowBranchId || SESSION.branchid),
          printMode: "1",
        });

        window.open(
          `/inventory/stn-outward-approve-report?${params.toString()}`,
          "_blank",
          "width=900,height=900,resizable=no,scrollbars=yes"
        );
      } else {
        toast.error("Failed to open view");
      }
    } catch (err) {
      toast.error("Failed to open view");
    }
  };

  /* ---------------- approve ---------------- */
  const handleApprove = async (row) => {
    const outwardId = row._id || row.fld_STN_OutwardID || row.STNOutwardID;
    const outwardNo = row._outwardNo || row.fld_STN_OutwardNo || row.outwardNo;
    const rowBranchId = row._branchId || row.fld_BranchID || row.branchId || "";
    const rowType = toStr(row._type || row.fld_Type || row.type || type, "Select");

    if (!outwardId || !outwardNo || !(rowBranchId || SESSION.branchid)) {
      toast.error("Required approval data missing");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/approve`,
        {
          outwardId,
          outwardNo,
          branchId: rowBranchId || SESSION.branchid,
          type: rowType,
        },
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        const sessionLike = res.data.sessionLike || {};
        const redirectTo = res.data.redirectTo || "/inventory/STNOutward_Direct";

        sessionStorage.setItem("stnoutward", toStr(sessionLike.stnoutward));
        sessionStorage.setItem("Sessionwordwardid", toStr(sessionLike.Sessionwordwardid));
        sessionStorage.setItem("AppBrnchId", toStr(sessionLike.AppBrnchId));
        sessionStorage.setItem("AppStat", toStr(sessionLike.AppStat, "1"));

        navigate(redirectTo);
      } else {
        toast.error("Approval init failed");
      }
    } catch (err) {
      toast.error("Failed to initiate approval");
    }
  };

  /* ---------------- print ---------------- */
  const handlePrint = async () => {
    try {
      const res = await axios.get(`${API_BASE}/printPayload`, {
        params: {
          branchID: SESSION.branchid,
          stnReqNo: stnOutwardNo,
          fromDate,
          toDate,
          requestedStore: inwardStore,
          status,
          type,
          userID: SESSION.loginuserid,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const params = new URLSearchParams({
          branchID: toStr(SESSION.branchid),
          stnReqNo: toStr(stnOutwardNo),
          fromDate: toStr(fromDate),
          toDate: toStr(toDate),
          requestedStore: toStr(inwardStore),
          status: toStr(status),
          type: toStr(type),
          userID: toStr(SESSION.loginuserid),
          printMode: "2",
        });

        window.open(
          `/inventory/stn-outward-approve-report?${params.toString()}`,
          "_blank",
          "width=900,height=900,resizable=no,scrollbars=yes"
        );
      } else {
        toast.error("Failed to open print view");
      }
    } catch (err) {
      toast.error("Failed to open print view");
    }
  };

  /* ---------------- clear filters ---------------- */
  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setInwardStore("Select");
    setStatus("Select");
    setType("Select");
    setStnOutwardNo("");
    setAutoSuggestions([]);
    setShowSuggestions(false);

    setTimeout(() => {
      axios
        .get(`${API_BASE}/list`, {
          params: {
            branchID: SESSION.branchid,
            stnReqNo: "",
            fromDate: "",
            toDate: "",
            requestedStore: "Select",
            status: "Select",
            type: "Select",
            userID: SESSION.loginuserid,
          },
          headers: commonHeaders,
        })
        .then((res) => {
          if (res.data?.success) {
            setRecords(res.data.rows || []);
            setGrandTotal(res.data.grandTotal || "0.00");
            setCurrency(res.data.currency || { code: "en-IN", symbol: "₹" });
          } else {
            setRecords([]);
            setGrandTotal("0.00");
          }
        })
        .catch(() => {
          setRecords([]);
          setGrandTotal("0.00");
        });
    }, 0);
  };

  /* ---------------- ui helpers ---------------- */
  const statusClass = (s) => {
    const val = String(s || "").toLowerCase();
    if (val === "approved") return "text-green-600 font-medium";
    if (val === "rejected") return "text-red-600 font-medium";
    return isDark ? "text-yellow-400 font-medium" : "text-yellow-600 font-medium";
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="STN OUTWARD APPROVE" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              STN Outward Approval
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {records.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? `border-gray-600 text-white hover:bg-gray-700${showFilters ? " bg-gray-700" : ""}`
                  : `border-gray-300 text-gray-700 hover:bg-gray-100${showFilters ? " bg-gray-100" : ""}`
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handlePrint}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
            >
              <FaPrint className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
              Search Filters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Inward Store
                </label>
                <select
                  value={inwardStore}
                  onChange={(e) => setInwardStore(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select Store</option>
                  {storeList.map((store, idx) => (
                    <option key={idx} value={store.fld_storeid || store.value}>
                      {store.fld_storename || store.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select Status</option>
                  <option value="Waiting">Waiting</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>

              <div className="relative">
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  STN Outward No
                </label>
                <input
                  type="text"
                  value={stnOutwardNo}
                  onChange={(e) => handleOutwardNoChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  placeholder="Enter outward number"
                />

                {showSuggestions && autoSuggestions.length > 0 && (
                  <ul
                    className={`absolute z-20 w-full border rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    {autoSuggestions.map((s, i) => (
                      <li
                        key={i}
                        className={`px-3 py-2 cursor-pointer text-sm ${
                          isDark ? "hover:bg-gray-600" : "hover:bg-gray-100"
                        }`}
                        onMouseDown={() => {
                          setStnOutwardNo(s);
                          setShowSuggestions(false);
                        }}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Type
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select Type</option>
                  <option value="2">Transfer</option>
                  <option value="1">Direct</option>
                </select>
              </div>
            </div>

            <div
              className={`flex justify-between items-center mt-6 pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <button
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <button
                onClick={handleSearch}
                disabled={loading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors disabled:opacity-60`}
              >
                <FaSearch className="w-4 h-4" />
                {loading ? "Searching..." : "Search Records"}
              </button>
            </div>
          </div>
        )}

        {records.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div className="p-4 border-b border-gray-200">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Approval Records ({records.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Sl No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Req No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">STN Outward No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">From Store</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">To Store</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">View</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Approve</th>
                  </tr>
                </thead>

                <tbody className={`divide-y divide-gray-200 ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  {records.map((row, index) => {
                    const rowStatus = row._status || row.status || row.fld_Status || "";
                    const amount = parseFloat(row._amount || row.Amount || 0) || 0;

                    return (
                      <tr
                        key={index}
                        className={`transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row._slno || index + 1}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row._reqNo || row.reqNo || row.fld_STN_ReqNo || ""}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {row._outwardNo || row.outwardNo || row.fld_STN_OutwardNo || ""}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {formatDate(row._date || row.date || row.fld_STN_OutwardDate)} 
                                                 </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row._fromStore || row.fromStore || row.fld_FromStore || ""}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row._toStore || row.toStore || row.fld_ToStore || ""}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {currency.symbol}{amount.toFixed(2)}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${statusClass(rowStatus)}`}>
                          {rowStatus}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleView(row)}
                            className="text-blue-500 hover:text-blue-700 transition-colors"
                            title="View Report"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {String(rowStatus).toLowerCase() === "waiting" ? (
                            <button
                              onClick={() => handleApprove(row)}
                              className="text-green-500 hover:text-green-700 transition-colors"
                              title="Approve"
                            >
                              <FaCheck className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={`p-4 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className={`text-sm font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Grand Total: {currency.symbol}{grandTotal}
              </div>
            </div>
          </div>
        ) : (
          <div className={`text-center py-12 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <p>No records found. Please use the filters to search for approval records.</p>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default STNOutwardApprove;